import React, { VoidFunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Accordion } from 'record-it-components/layout/Accordion/Accordion';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { DownloadUtils } from 'record-it-utils/utils/DownloadUtils/DownloadUtils';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { EntityName } from 'record-it-time-common/EntityName';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';

import { ExportYearSelect } from '../ExportYearSelect/ExportYearSelect';
import { MonthSelect } from '../../input/MonthSelect/MonthSelect';
import {
  ExportWorkTimeBaseDialog,
  ExportWorkTimeBaseDialogProps
} from '../ExportWorkTimeBaseDialog/ExportWorkTimeBaseDialog';
import { Month, allMonths } from '../../input/MonthSelect/Month';
import styles from '../ExportWorkTimeBaseDialog/ExportWorkTimeBaseDialog.module.css';
import { ApiAdapter } from '../../../classes/ApiAdapter/ApiAdapter';
import { UserGroupSelect } from '../../input/UserGroupSelect/UserGroupSelect';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { ConstructionSiteSelect } from '../../input/ConstructionSiteSelect/ConstructionSiteSelect';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

export type ExportAdminWorkTimeDialogProps = PropsWithoutChildren<
  Omit<ExportWorkTimeBaseDialogProps, 'showLoading'>
>;

export const ExportAdminWorkTimeDialog: VoidFunctionComponent<ExportAdminWorkTimeDialogProps> =
  React.memo((props) => {
    const { t } = useTranslation();

    const { getRequiredCurrentUserPermissions } = useCurrentUser();
    const userGroups =
      getRequiredCurrentUserPermissions().getUserGroupsForExport();

    const constructionSites = useEntities(
      EntityName.ConstructionSite,
      'getAll'
    );

    const [constructionSiteExportOpen, setConstructionSiteExportOpen] =
      useState(true);

    const [
      constructionSiteSummaryExportOpen,
      setConstructionSiteSummaryExportOpen
    ] = useState(true);

    const [activitiesExportOpen, setActivitiesExportOpen] = useState(true);

    const [allTimesheetsExportOpen, setAllTimesheetsExportOpen] =
      useState(true);

    const [fullExportOpen, setFullExportOpen] = useState(true);

    const [allTimesheetsYear, setAllTimesheetsYear] = useState<number>(
      new Date().getFullYear()
    );
    const [allTimesheetsMonth, setAllTimesheetsMonth] = useState<Month>(
      new Date().getMonth() + 1
    );
    const [allTimesheetsUserGroupId, setAllTimesheetsUserGroupId] = useState<
      string | null
    >(null);

    const [loading, setLoading] = useState(false);

    const [constructionSiteYear, setConstructionSiteYear] = useState<number>(
      new Date().getFullYear()
    );

    const [constructionSiteUserGroupId, setConstructionSiteUserGroupId] =
      useState<string | null>(userGroups[0]?.id ?? null);

    const [constructionSiteId, setConstructionSiteId] = useState<string | null>(
      null
    );

    const constructionSiteRepository = useEntityRepository(
      EntityName.ConstructionSite
    );

    const constructionSiteExportEnabled = !!constructionSiteUserGroupId;

    const constructionSiteSummaryExportEnabled = !!constructionSiteId;

    const [activitiesYear, setActivitiesYear] = useState<number>(
      new Date().getFullYear()
    );

    const [activitiesUserGroupId, setActivitiesUserGroupId] = useState<
      string | null
    >(userGroups[0]?.id ?? null);

    const activitiesExportEnabled = !!activitiesUserGroupId;

    const [fullExportYear, setFullExportYear] = useState<number>(
      new Date().getFullYear()
    );

    const [fullExportUserGroupId, setFullExportUserGroupId] = useState<
      string | null
    >(userGroups[0]?.id ?? null);

    const fullExportEnabled = !!fullExportUserGroupId;

    const getConstructionSiteExportFileName = (year: number): string => {
      return t(
        'components.workTime.ExportAdminWorkTimeDialog.exports.constructionSite.fileName',
        { year }
      );
    };

    const getConstructionSiteSummaryExportFileName = (name: string): string => {
      return t(
        'components.workTime.ExportAdminWorkTimeDialog.exports.constructionSiteSummary.fileName',
        { name }
      );
    };

    const getActivitiesExportFileName = (year: number): string => {
      return t(
        'components.workTime.ExportAdminWorkTimeDialog.exports.activities.fileName',
        { year }
      );
    };

    const handleConstructionSiteExportClick = useEventCallback(() => {
      assertNotNullOrUndefined(
        constructionSiteUserGroupId,
        'cannot handleConstructionSiteExportCLick without constructionSiteUserGroup'
      );

      setLoading(true);

      void ApiAdapter.exportWorkTimeConstructionSite({
        year: constructionSiteYear,
        userGroupId: constructionSiteUserGroupId
      })
        .then((response) => {
          const filename =
            getConstructionSiteExportFileName(constructionSiteYear);
          DownloadUtils.download(filename, response);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    const handleConstructionSiteSummaryExportClick = useEventCallback(() => {
      assertNotNullOrUndefined(
        constructionSiteId,
        'cannot handleConstructionSiteSummaryExportClick without constructionSiteId'
      );

      setLoading(true);

      void ApiAdapter.exportWorkTimeConstructionSiteSummary({
        constructionSiteId
      })
        .then((response) => {
          const constructionSiteName =
            constructionSiteRepository.getRequiredById(constructionSiteId).name;

          const fileName =
            getConstructionSiteSummaryExportFileName(constructionSiteName);
          DownloadUtils.download(fileName, response);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    const handleActivitiesExportClick = useEventCallback(() => {
      assertNotNullOrUndefined(
        activitiesUserGroupId,
        'cannot handleActivitiesExportClick without activitiesUserGroupId'
      );

      setLoading(true);

      void ApiAdapter.exportWorkTimeActivities({
        year: activitiesYear,
        userGroupId: activitiesUserGroupId
      })
        .then((response) => {
          const filename = getActivitiesExportFileName(activitiesYear);
          DownloadUtils.download(filename, response);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    const handleAllTimesheetsExportClick = useEventCallback(() => {
      assertNotNullOrUndefined(
        allTimesheetsUserGroupId,
        'cannot handleAllTimesheetsExportClick without allTimesheetsUserGroupId'
      );

      setLoading(true);

      void ApiAdapter.exportWorkTimeAllTimesheets({
        daterange: {
          year: allTimesheetsYear,
          month: allTimesheetsMonth
        },
        forUserGroupId: allTimesheetsUserGroupId
      })
        .then((response) => {
          const filename = t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.allTimesheetsForUserGroup.fileName',
            {
              year: allTimesheetsYear,
              month: StringUtils.padLeft(allTimesheetsMonth.toString(), 2)
            }
          );
          DownloadUtils.download(filename, response);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    const handleFullExportClick = useEventCallback(() => {
      assertNotNullOrUndefined(
        fullExportUserGroupId,
        'cannot handleFullExportClick without fullExportUserGroupId'
      );

      setLoading(true);

      void ApiAdapter.exportWorkTimeFullExport({
        userGroupId: fullExportUserGroupId,
        year: fullExportYear
      })
        .then((response) => {
          const fileName = t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.fullExport.fileName',
            {
              year: fullExportYear
            }
          );

          DownloadUtils.download(fileName, response);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    return (
      <ExportWorkTimeBaseDialog
        showLoading={loading}
        {...props}
      >
        <Accordion
          summary={t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.constructionSite.title'
          )}
          open={constructionSiteExportOpen}
          onToggle={setConstructionSiteExportOpen}
        >
          <FormGroup>
            <div
              className={styles.exportInputForm}
              data-testid="ExportAdminWorkTimeDialog.ConstructionSiteExport"
            >
              <ExportYearSelect
                year={constructionSiteYear}
                onYearSelected={setConstructionSiteYear}
              ></ExportYearSelect>
              <UserGroupSelect
                userGroupId={constructionSiteUserGroupId}
                onUserGroupChanged={setConstructionSiteUserGroupId}
                availableUserGroups={userGroups}
              ></UserGroupSelect>
            </div>
          </FormGroup>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            disabled={!constructionSiteExportEnabled}
            onClick={handleConstructionSiteExportClick}
            data-testid="ExportAdminWorkTimeDialog.ConstructionSite.ExportButton"
          >
            {t('components.workTime.ExportWorkTimeBaseDialog.export')}
          </ButtonStandard>
        </Accordion>

        <Accordion
          summary={t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.constructionSiteSummary.title'
          )}
          open={constructionSiteSummaryExportOpen}
          onToggle={setConstructionSiteSummaryExportOpen}
        >
          <FormGroup>
            <div className={styles.exportInputForm}>
              <ConstructionSiteSelect
                availableConstructionSites={constructionSites}
                constructionSiteId={constructionSiteId}
                onConstructionSiteChanged={setConstructionSiteId}
              ></ConstructionSiteSelect>
            </div>
          </FormGroup>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            disabled={!constructionSiteSummaryExportEnabled}
            onClick={handleConstructionSiteSummaryExportClick}
          >
            {t('components.workTime.ExportWorkTimeBaseDialog.export')}
          </ButtonStandard>
        </Accordion>

        <Accordion
          summary={t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.activities.title'
          )}
          open={activitiesExportOpen}
          onToggle={setActivitiesExportOpen}
        >
          <FormGroup>
            <div className={styles.exportInputForm}>
              <ExportYearSelect
                year={activitiesYear}
                onYearSelected={setActivitiesYear}
              ></ExportYearSelect>
              <UserGroupSelect
                userGroupId={activitiesUserGroupId}
                onUserGroupChanged={setActivitiesUserGroupId}
                availableUserGroups={userGroups}
              ></UserGroupSelect>
            </div>
          </FormGroup>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            disabled={!activitiesExportEnabled}
            onClick={handleActivitiesExportClick}
          >
            {t('components.workTime.ExportWorkTimeBaseDialog.export')}
          </ButtonStandard>
        </Accordion>

        <Accordion
          summary={t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.allTimesheetsForUserGroup.title'
          )}
          open={allTimesheetsExportOpen}
          onToggle={setAllTimesheetsExportOpen}
        >
          <FormGroup>
            <div
              className={styles.exportInputForm}
              data-testid="ExportAdminWorkTimeDialog.AllTimesheets"
            >
              <ExportYearSelect
                year={allTimesheetsYear}
                onYearSelected={setAllTimesheetsYear}
              ></ExportYearSelect>

              <MonthSelect
                availableMonths={allMonths}
                month={allTimesheetsMonth}
                onMonthSelected={setAllTimesheetsMonth}
              ></MonthSelect>

              <UserGroupSelect
                userGroupId={allTimesheetsUserGroupId}
                onUserGroupChanged={setAllTimesheetsUserGroupId}
                availableUserGroups={userGroups}
                data-testid="ExportAdminWorkTimeDialog.AllTimesheets.UserGroupSelect"
              ></UserGroupSelect>
            </div>
          </FormGroup>

          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleAllTimesheetsExportClick}
            data-testid="ExportAdminWorkTimeDialog.AllTimesheets.ExportButton"
          >
            {t('components.workTime.ExportWorkTimeBaseDialog.export')}
          </ButtonStandard>
        </Accordion>

        <Accordion
          summary={t(
            'components.workTime.ExportAdminWorkTimeDialog.exports.fullExport.title'
          )}
          open={fullExportOpen}
          onToggle={setFullExportOpen}
        >
          <FormGroup>
            <div className={styles.exportInputForm}>
              <ExportYearSelect
                year={fullExportYear}
                onYearSelected={setFullExportYear}
              ></ExportYearSelect>

              <UserGroupSelect
                userGroupId={fullExportUserGroupId}
                onUserGroupChanged={setFullExportUserGroupId}
                availableUserGroups={userGroups}
              ></UserGroupSelect>
            </div>
          </FormGroup>

          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleFullExportClick}
            disabled={!fullExportEnabled}
          >
            {t('components.workTime.ExportWorkTimeBaseDialog.export')}
          </ButtonStandard>
        </Accordion>
      </ExportWorkTimeBaseDialog>
    );
  });
